import React, { useMemo, useRef, useCallback } from 'react';
import { Box, Form } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { BusinessNameSearch } from '../../../../../../components/BusinessNameSearch/BusinessNameSearch';
import type {
  BusinessSearchResult,
  ModuleFileReference,
} from '../../../../../../api/services/lupp/order/dto.order';
import { createFinanceProviderFormFields } from './formFields';
import type { FinanceProviderDetailsData } from '../types';
import { FileHubApi } from '../../../../../../api/services/lupp/lupp.service';
import {
  getFilesByCategory,
  mapToInitialFiles,
} from '../../../../../../api/services/lupp/order/transformers.order';
import { DOCUMENT_CATEGORY } from '../../../../../../constants/constants';

/** Stable custom components configuration to prevent unnecessary re-renders */
const customComponents = {
  BusinessNameSearch: BusinessNameSearch as React.ComponentType<Record<string, unknown>>,
};

type FinanceProviderDetailsProps = {
  theme: 'light' | 'dark';
  data?: FinanceProviderDetailsData;
  labels: Record<string, string>;
  onSave?: (data: FinanceProviderDetailsData) => void;
  dataTestId?: string;
  moduleFileReferenceDtoList?: ModuleFileReference[];
};

/**
 * FinanceProviderDetails component displays and manages finance provider information.
 * Renders a form with finance provider details and financing agreement document viewer.
 * Supports document download via FileHub integration with XSS-safe link rendering.
 *
 * @component
 * @param {Object} props - Component props
 * @param {'light' | 'dark'} props.theme - Theme for the component (light or dark)
 * @param {FinanceProviderDetailsData} [props.data] - Finance provider data to display
 * @param {Record<string, string>} props.labels - i18n labels for form fields and messages
 * @param {Function} [props.onSave] - Callback fired when form is saved with updated data
 * @param {string} [props.dataTestId='finance-provider-details'] - Test ID for the root element
 * @param {Array} [props.moduleFileReferenceDtoList] - List of file references including financing agreement documents
 * @returns {JSX.Element} Rendered FinanceProviderDetails component
 *
 * @example
 * ```tsx
 * <FinanceProviderDetails
 *   theme="dark"
 *   data={financeProviderData}
 *   labels={labels}
 *   onSave={handleSave}
 *   dataTestId="finance-provider-details"
 *   moduleFileReferenceDtoList={orderData.moduleFileReferenceDtoList}
 * />
 * ```
 */
const FinanceProviderDetails: React.FC<FinanceProviderDetailsProps> = ({
  theme,
  data,
  labels,
  onSave,
  dataTestId = 'finance-provider-details',
  moduleFileReferenceDtoList,
}) => {
  const formRef = useRef<FormRef>(null);

  // Memoize default values to prevent unnecessary re-renders
  const defaultValues = useMemo(
    () => ({
      name: data?.name,
      address: data?.address,
      city: data?.city,
      postalCode: data?.postalCode,
      vat: data?.vat,
    }),
    [data?.name, data?.address, data?.city, data?.postalCode, data?.vat]
  );

  const setFormValue = useCallback((name: string, value: unknown) => {
    formRef.current?.setValue(name, value, { shouldValidate: true, shouldDirty: true });
  }, []);

  const handleFinanceProviderChange = useCallback(
    (business: BusinessSearchResult | null) => {
      if (business) {
        // Batch form updates
        setFormValue('name', business.name);
        setFormValue('address', business.address);
        setFormValue('city', business.city);
        setFormValue('postalCode', business.postalCode);
        // Note: VAT number is not provided by D&B API, so it remains empty
      }
    },
    [setFormValue]
  );

  /**
   * Downloads a document from FileHub and opens it in a new tab
   * @param fileId - The FileHub file identifier
   */
  const downloadAndOpenDocument = useCallback(async (fileId: string) => {
    // Validate input
    if (!fileId || typeof fileId !== 'string') {
      return;
    }

    try {
      const response = await FileHubApi.instance.get<string>(
        `/shared/filehub/files/${fileId}/download`
      );
      const downloadUrl = response.data;

      // Silently return if download URL is empty (business logic validation, not an exception)
      if (!downloadUrl) {
        return;
      }

      window.open(downloadUrl, '_blank');
    } catch (error) {
      // Silently fail - API errors are handled by the FileHub service interceptor
      // No user notification needed as the document simply won't open
    }
  }, []);

  /**
   * Event handler for click events on financing agreement links.
   * Intercepts clicks on anchor tags with data-filehub-id attributes.
   */
  const handleFinancingAgreementClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[data-filehub-id]');

      if (!(link instanceof HTMLAnchorElement)) {
        return;
      }

      event.preventDefault();

      const fileId = link.dataset.filehubId;

      if (fileId) {
        void downloadAndOpenDocument(fileId);
      }
    },
    [downloadAndOpenDocument]
  );

  // Find the Finance Agreement document from the file reference list using transformer
  const financingAgreementDoc = useMemo((): { fileId: string; fileName: string } | undefined => {
    const financeAgreementFiles = getFilesByCategory(
      moduleFileReferenceDtoList,
      DOCUMENT_CATEGORY.FINANCE_AGREEMENT
    );

    // Use mapToInitialFiles to transform and filter valid documents
    const initialFiles = mapToInitialFiles(financeAgreementFiles);

    // Find first file with valid (non-whitespace) fileId
    const validFile = initialFiles.find((file) => file.fileId.trim());

    if (!validFile) {
      return undefined;
    }

    // Ensure fileName is valid, fallback to category name if whitespace-only
    return {
      fileId: validFile.fileId.trim(),
      fileName: validFile.fileName.trim() || DOCUMENT_CATEGORY.FINANCE_AGREEMENT,
    };
  }, [moduleFileReferenceDtoList]);

  const financeProviderFormFields = useMemo(
    () =>
      createFinanceProviderFormFields(
        labels,
        defaultValues,
        theme,
        handleFinanceProviderChange,
        financingAgreementDoc
      ),
    [labels, defaultValues, theme, handleFinanceProviderChange, financingAgreementDoc]
  );

  const handleSuccess = useCallback(
    (formData: unknown) => {
      const typedData = formData as FinanceProviderDetailsData;
      onSave?.(typedData);
    },
    [onSave]
  );

  return (
    <Box data-testid={dataTestId} onClick={handleFinancingAgreementClick}>
      {/* Finance Provider Form */}
      <Form
        id="finance-provider-form"
        ref={formRef}
        theme={theme === 'dark' ? 'light' : 'dark'}
        formFields={financeProviderFormFields}
        onSuccess={handleSuccess}
        onError={() => {}}
        isFluid={true}
        customComponents={customComponents}
      />
    </Box>
  );
};

FinanceProviderDetails.displayName = 'FinanceProviderDetails';

export default FinanceProviderDetails;
